import * as React from 'react';
import type { LegendProps } from 'recharts';
import { cn } from '@/shared/lib/utils';
import { Text } from '../text';
import { getPayloadConfigFromPayload, useChart } from './types';

type ChartLegendContentProps = React.ComponentProps<'div'> &
  Pick<LegendProps, 'payload' | 'verticalAlign'> & {
    hideIcon?: boolean;
    nameKey?: string;
  };

/**
 * チャート凡例のコンテンツ
 *
 * Recharts の Legend content として使用する。
 * ChartConfig に基づいてラベルと色インジケーターを自動表示する。
 */
export const ChartLegendContent = React.forwardRef<HTMLDivElement, ChartLegendContentProps>(
  ({ className, hideIcon = false, payload, verticalAlign = 'bottom', nameKey }, ref) => {
    const { config } = useChart();

    if (!payload?.length) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-center gap-4',
          verticalAlign === 'top' ? 'pb-3' : 'pt-3',
          className,
        )}
      >
        {payload
          .filter((item) => item.type !== 'none')
          .map((item) => {
            const key = `${nameKey || item.dataKey || 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);

            return (
              <div
                key={`${item.dataKey ?? item.value}`}
                className={cn(
                  'flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground',
                )}
              >
                {itemConfig?.icon && !hideIcon ? (
                  <itemConfig.icon />
                ) : (
                  <div
                    className="h-2 w-2 shrink-0 rounded-xs"
                    style={{
                      backgroundColor: item.color,
                    }}
                  />
                )}
                {typeof itemConfig?.label === 'string' ? (
                  <Text as="span">{itemConfig.label}</Text>
                ) : (
                  itemConfig?.label
                )}
              </div>
            );
          })}
      </div>
    );
  },
);
ChartLegendContent.displayName = 'ChartLegendContent';
